import React, { useRef, useEffect } from "react";
import { styled } from "styled-components";
import { RxCross2 } from "react-icons/rx";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

const Credits = ({ openModal, closeModal }) => {
  const { t } = useTranslation();
  const ref = useRef();

  useEffect(() => {
    if (openModal) {
      ref.current?.showModal();
    } else {
      ref.current?.close();
    }
  }, [openModal]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "Escape") closeModal();
  };

  return (
    <AnimatePresence>
      {openModal && (
        <Overlay
          as={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={closeModal} /* clic hors modale = fermeture */
        >
          <DialogContainer
            ref={ref}
            onCancel={closeModal}
            as={motion.dialog}
            initial={{ opacity: 0, scale: 0.93, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.93, y: 12 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            onClick={(e) =>
              e.stopPropagation()
            } /* évite la fermeture au clic interne */
          >
            {/* Bouton fermeture */}
            <CloseBtn
              tabIndex={0}
              onClick={closeModal}
              onKeyDown={handleKeyDown}
              aria-label="Fermer"
            >
              <RxCross2 />
            </CloseBtn>

            {/* Titre */}
            <ModalTitle>{t("footer.credits")}</ModalTitle>

            {/* Section photos */}
            <Block>
              <SectionLabel>Photos</SectionLabel>
              <CreditList>
                <li>Pawel Czerwinski</li>
                <li>Cassi Josh</li>
              </CreditList>
            </Block>

            {/* Texte usage */}
            <UsageText>{t("footer.credits_modal.use")}</UsageText>
          </DialogContainer>
        </Overlay>
      )}
    </AnimatePresence>
  );
};

export default Credits;

/* ─── Styles ──────────────────────────────────────────────────────────────── */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DialogContainer = styled.dialog`
  position: relative;
  width: min(480px, 90vw);
  padding: 2.5rem 2rem 2rem;
  border: none;
  border-radius: 18px;
  background: rgba(18, 10, 10, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.07);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6);
  color: #fafafa;

  /* reset dialog natif */
  &::backdrop {
    display: none;
  }

  * {
    font-family: "Gowun Batang", serif;
  }
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #a8a29e;
  font-size: 1.1rem;
  cursor: pointer;
  transition:
    color 0.2s ease,
    background 0.2s ease,
    border-color 0.2s ease;

  &:hover {
    color: #fafafa;
    background: rgba(180, 1, 18, 0.15);
    border-color: rgba(180, 1, 18, 0.4);
  }
`;

const ModalTitle = styled.h1`
  font-size: 1.6rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 1.5rem;
  background: linear-gradient(to right, #ffffff, rgba(180, 1, 18, 0.9));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  position: relative;
  display: inline-block;
  padding-bottom: 0.4rem;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 50%;
    height: 2px;
    background: linear-gradient(90deg, rgba(180, 1, 18, 0.7), transparent);
    border-radius: 2px;
  }
`;

const Block = styled.div`
  margin-bottom: 1.25rem;
`;

const SectionLabel = styled.h2`
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #a8a29e;
  margin-bottom: 0.5rem;
  padding-left: 0.6rem;
  border-left: 2px solid rgba(180, 1, 18, 0.6);
`;

const CreditList = styled.ul`
  padding-left: 1.2rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  li {
    list-style-type: disc;
    font-size: 1rem;
    color: #d4d4d4;
    line-height: 1.6;
  }
`;

const UsageText = styled.p`
  font-size: 0.88rem;
  color: #6b6b6b;
  line-height: 1.65;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  padding-top: 1rem;
  margin-top: 0.5rem;
`;
